import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import type { TextFieldProps } from '@components/moleculs/text-field/types';
import cs from 'classnames';
import { styled } from 'styled-components';

import { Input } from './styles';

const Molecule = forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
    const {
        className,
        InputProps,
        inputProps,
        slotProps,
        required,
        fieldState,
        hint,
        helperText,
        disabled,
        tooltipProps,
        inputSize = 'm',
        helperTextStatic = false,
        ...rest
    } = props;
    const errorText = useMemo(() => {
        if (fieldState?.error?.message) {
            return fieldState.error.message;
        }
        if (helperText && rest.error) {
            return helperText;
        }

        return '';
    }, [fieldState, rest.error, helperText]);

    // Preserve upstream slots and MERGE (not overwrite) `input`/`htmlInput` so the molecule's
    // InputProps/inputProps API layers on top. Autocomplete's `renderInput` spreads `{...params}`,
    // carrying `slotProps.input` (Popper-anchor ref, the dropdown `endAdornment`,
    // `MuiAutocomplete-inputRoot` class, mouse handlers) and `slotProps.htmlInput` (the native
    // <input> ref from getInputProps). Overwriting these dropped the dropdown arrow / anchor ref
    // (broke custom pickers) and the input ref (MUI's "Unable to find the input element" error).
    // MUI's slot props may be an object OR an ownerState callback; this molecule only ever gets the
    // object form, so ignore the (unused) callback form when merging.
    const upstreamInput = typeof slotProps?.input === 'function' ? undefined : slotProps?.input;
    const upstreamHtmlInput =
        typeof slotProps?.htmlInput === 'function' ? undefined : slotProps?.htmlInput;

    // Single cast: TextField's `slotProps` is a union of per-variant overloads, and the upstream
    // `input`/`htmlInput` objects (from Autocomplete's `renderInput` params) carry loosely-typed
    // slot refs that can't be reconciled structurally against every variant. The merge itself is
    // correct at runtime; we assert the resulting shape as the molecule's own `slotProps` type.
    const mergedSlotProps = {
        ...slotProps,
        input: {
            // `disableUnderline` exists on FilledInput/Input only. The default variant is
            // `outlined`, and OutlinedInput does not know the prop, so it forwards it to the DOM.
            ...(rest.variant === 'filled' || rest.variant === 'standard'
                ? { disableUnderline: true }
                : {}),
            ...upstreamInput,
            ...InputProps,
        },
        htmlInput: {
            ...upstreamHtmlInput,
            ...inputProps,
        },
    } as TextFieldProps['slotProps'];

    return (
        <Input
            disabled={disabled}
            $inputSize={inputSize}
            $isMultiline={rest.multiline as boolean}
            $helperTextStatic={helperTextStatic}
            ref={ref}
            {...rest}
            slotProps={mergedSlotProps}
            className={cs(className, {
                'Mui-error-container': !!errorText || props?.error,
            })}
            error={!!errorText}
            helperText={
                (!!hint || !!helperText) && (
                    <span className='helper-container'>
                        {!!hint && <span className='hint'>{hint}</span>}
                        {helperText}
                    </span>
                )
            }
        />
    );
});

export const TextField = styled(Molecule)({});
